import { useCallback, useEffect, useState } from 'react';

import config from '@/config';
import ChannelsService from '@/services/channels.service';
import fetchWrapper from '@/services/fetchWrapper.service';
import UserService from '@/services/user.service';

import invoicesService from '../services/invoices.service';

function indexSuarteInvoices(rows) {
  return rows.reduce((invoicesByOrderId, invoice) => {
    const orderId = String(invoice?.order_id ?? '').trim();
    const invoiceUrl = invoice?.invoice_url ?? null;
    if (orderId && invoiceUrl) invoicesByOrderId[orderId] = invoiceUrl;
    return invoicesByOrderId;
  }, {});
}

function indexGalleryInvoices(rows) {
  return rows.reduce((invoicesByOrderId, invoice) => {
    const orderId = String(
      invoice?.metadata?.orderId
      ?? invoice?.builder?.orderId
      ?? invoice?.builder?.source?.orderId
      ?? ''
    ).trim();
    const invoiceUrl = invoice?.document_url ?? invoice?.invoice_url ?? null;
    if (orderId && invoiceUrl) invoicesByOrderId[orderId] = invoiceUrl;
    return invoicesByOrderId;
  }, {});
}

export default function useProfilePaymentsData() {
  const [profileData, setProfileData] = useState(null);
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileError, setProfileError] = useState(null);
  const [profileVersion, setProfileVersion] = useState(0);
  const [userPaymentsPayload, setUserPaymentsPayload] = useState(null);
  const [userPaymentsLoading, setUserPaymentsLoading] = useState(false);
  const [userPaymentsError, setUserPaymentsError] = useState(null);
  const [userPaymentsVersion, setUserPaymentsVersion] = useState(0);
  const [channelMembershipSummary, setChannelMembershipSummary] = useState(null);
  const [channelMembershipLoading, setChannelMembershipLoading] = useState(false);
  const [channelMembershipError, setChannelMembershipError] = useState(null);
  const [channelMembershipVersion, setChannelMembershipVersion] = useState(0);
  const [suarteInvoicesByOrderId, setSuarteInvoicesByOrderId] = useState({});
  const [galleryInvoicesByOrderId, setGalleryInvoicesByOrderId] = useState({});

  useEffect(() => {
    let mounted = true;
    setProfileLoading(true);
    setProfileError(null);
    fetchWrapper.get(`${config.apis.api.url}/user/me`, { injectToken: true })
      .then(({ response, data }) => {
        if (!mounted) return;
        if (response?.ok && data) setProfileData(data);
        else setProfileError(data?.message ?? 'Unable to load profile.');
      })
      .catch((error) => mounted && setProfileError(error?.message ?? 'Unable to load profile.'))
      .finally(() => mounted && setProfileLoading(false));
    return () => { mounted = false; };
  }, [profileVersion]);

  useEffect(() => {
    const userId = profileData?._id ?? null;
    if (!userId) {
      setUserPaymentsPayload(null);
      setUserPaymentsError(null);
      setUserPaymentsLoading(false);
      return undefined;
    }
    let mounted = true;
    setUserPaymentsLoading(true);
    setUserPaymentsError(null);
    UserService.getUserPayments(userId)
      .then(({ response, data }) => {
        if (!mounted) return;
        if (response?.ok && data) setUserPaymentsPayload(data);
        else {
          setUserPaymentsError(data?.message ?? 'Unable to load payments.');
          setUserPaymentsPayload(null);
        }
      })
      .catch((error) => {
        if (!mounted) return;
        setUserPaymentsError(error?.message ?? 'Unable to load payments.');
        setUserPaymentsPayload(null);
      })
      .finally(() => mounted && setUserPaymentsLoading(false));
    return () => { mounted = false; };
  }, [profileData?._id, userPaymentsVersion]);

  useEffect(() => {
    const userId = profileData?._id ?? null;
    if (!userId) {
      setChannelMembershipSummary(null);
      setChannelMembershipError(null);
      setChannelMembershipLoading(false);
      return undefined;
    }
    let mounted = true;
    setChannelMembershipLoading(true);
    setChannelMembershipError(null);
    ChannelsService.getMembershipSummary()
      .then(({ response, data }) => {
        if (!mounted) return;
        if (response?.ok && data) setChannelMembershipSummary(data);
        else {
          setChannelMembershipSummary(null);
          setChannelMembershipError(data?.message ?? 'Unable to load channel subscriptions.');
        }
      })
      .catch((error) => {
        if (!mounted) return;
        setChannelMembershipSummary(null);
        setChannelMembershipError(error?.message ?? 'Unable to load channel subscriptions.');
      })
      .finally(() => mounted && setChannelMembershipLoading(false));
    return () => { mounted = false; };
  }, [profileData?._id, channelMembershipVersion]);

  useEffect(() => {
    if (!profileData?._id) {
      setSuarteInvoicesByOrderId({});
      setGalleryInvoicesByOrderId({});
      return undefined;
    }
    let mounted = true;
    setSuarteInvoicesByOrderId({});
    setGalleryInvoicesByOrderId({});
    invoicesService.getSuarteInvoices()
      .then(({ response, data }) => mounted && response?.ok && setSuarteInvoicesByOrderId(indexSuarteInvoices(Array.isArray(data) ? data : [])))
      .catch(() => null);
    invoicesService.getGalleryInvoices()
      .then(({ response, data }) => mounted && response?.ok && setGalleryInvoicesByOrderId(indexGalleryInvoices(Array.isArray(data) ? data : [])))
      .catch(() => null);
    return () => { mounted = false; };
  }, [profileData?._id]);

  const refreshProfile = useCallback(() => {
    setProfileVersion((value) => value + 1);
    setUserPaymentsVersion((value) => value + 1);
    setChannelMembershipVersion((value) => value + 1);
  }, []);

  return {
    profileData, profileLoading, profileError, userPaymentsPayload, userPaymentsLoading,
    userPaymentsError, channelMembershipSummary, channelMembershipLoading, channelMembershipError,
    setChannelMembershipError, suarteInvoicesByOrderId, setSuarteInvoicesByOrderId,
    galleryInvoicesByOrderId, refreshProfile,
  };
}
